import { Application, Controller } from '@hotwired/stimulus';
import { BarcodeDetectorPolyfill } from '@undecaf/barcode-detector-polyfill';
import { getCameraDevices, hasCameraPermissions } from '../../500-common/util';

export const enum ScannerState {
    NoPermissions,
    NoCamera,
    Running,
    Stopped,
}

export class ScannerController extends Controller {
    static targetsKeys = ['video', 'cameraSwitch', 'scanner'] as const;
    static targets = [...this.targetsKeys];
    static classes = ['noCamera', 'noPermissions', 'running'];

    static get shouldLoad() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
            console.info('enumerateDevices() not supported.');
            return false;
        }

        if (!('BarcodeDetector' in window)) {
            window['BarcodeDetector'] = BarcodeDetectorPolyfill;
            return true;
        } else {
            return true;
        }
    }

    declare readonly videoTarget: HTMLVideoElement;
    declare readonly scannerTarget: HTMLElement;
    declare readonly noCameraClass: string;
    declare readonly noPermissionsClass: string;
    declare readonly runningClass: string;

    /**
     * barcode detector instance, the native one or the polyfilled one
     */
    barcodeDetector = null;

    /**
     * If true, the scanner/camera functionality is currently running
     */
    active = false;

    /**
     * The current camera to use for the scanner
     */
    currentDevice = null;

    /**
     * Does the users current device has a camera?
     */
    hasDevice = false;

    /**
     * Store the scanned values to prevent multiple subsequent scans during the time the user
     * still holds the sheet into the camera
     */
    scannedValues: string[] = [];

    mediaStream: MediaStream = null;

    /**
     * Register the controller in the given application
     */
    static register(application: Application) {
        application.register('scanner', this);
    }

    async connect() {
        if (this.scannerTarget.classList.contains('js')) {
            return;
        } else {
            this.scannerTarget.classList.add('js');
        }

        const devices = await getCameraDevices();
        if (!devices?.length) {
            this.scannerTarget.classList.add(this.noCameraClass);
            this.dispatch('stateChange', { detail: ScannerState.NoPermissions });
        } else if (!(await hasCameraPermissions())) {
            this.scannerTarget.classList.add(this.noPermissionsClass);
            this.dispatch('stateChange', { detail: ScannerState.NoPermissions });
        } else {
            /** @ts-ignore */
            this.barcodeDetector = new BarcodeDetector();
            this.startVideo();
        }

        // We currently don't use this logic but keep it here in case
        // a camera switch becomes useful.
        // const devices = await this.getCameraDevices();
        // this.hasDevice = devices.length > 0;

        // if (this.hasDevice) {
        //     this.currentDevice = devices[0];
        // }
    }

    async startVideo() {
        this.scannerTarget.classList.add(this.runningClass);
        this.dispatch('stateChange', { detail: ScannerState.Running });

        this.mediaStream = await ScannerController.getMediaStream();

        this.videoTarget.srcObject = this.mediaStream;
        this.videoTarget.autoplay = true;
        this.videoTarget.hidden = false;

        this.active = true;

        this.renderLoop();
    }

    stopVideo() {
        this.active = false;
        this.scannerTarget.classList.remove(this.runningClass);
        this.dispatch('stateChange', { detail: ScannerState.Stopped });

        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach((track) => track.stop());
            this.mediaStream = null;
        }
    }

    renderLoop() {
        if (this.active) {
            requestAnimationFrame(() => this.renderLoop());
            void this.render();
        }
    }

    async render() {
        try {
            const barcodes = await this.barcodeDetector.detect(this.videoTarget);

            barcodes.forEach((barcode) => {
                if (!this.scannedValues.includes(barcode.rawValue)) {
                    console.log(`Scanned: ${barcode.rawValue}`);
                    this.scannedValues.push(barcode.rawValue);
                    this.dispatch('match', { detail: { value: barcode.rawValue } });
                }
            });
        } catch (error) {
            if (error instanceof DOMException) {
                console.info('Video no ready to scan yet.');
            } else {
                console.error(error);
            }
        }
    }

    private static async getMediaStream() {
        return await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
        });
    }
}
